import {
  useChainedCommands,
  useCommands,
  useEditorView,
} from '@remirror/react';
import React, { useCallback, useEffect, useState } from 'react';
import { isElementDomNode, NodeWithPosition } from 'remirror';
import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useColorModeValue as mode,
  useDisclosure,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import {
  CompassIcon,
  H1Icon,
  H2Icon,
  H3Icon,
  HorizontalLineIcon,
  ListOlIcon,
  ListUlIcon,
  TextIcon,
  TodoIcon,
} from 'components/icons';
import { AddImageBlock } from './AddImageBlock';
import { AddFileBlock } from './AddFileBlock';
import { useActiveLayout } from 'utils/LayoutProvider';
import { useI18n } from 'utils/i18n';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SetUrlModal as CreateLinkCardModal } from '../LinkCardExtension/LinkCard';

interface AddBlocksToolbarProps {
  pageId?: string;
}

export default function AddBlocksToolbar({ pageId }: AddBlocksToolbarProps) {
  const view = useEditorView();
  const chain = useChainedCommands();
  const commands = useCommands();

  const [openMenu, _setOpenMenu] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [offsetX, setOffsetX] = useState(view.dom.getBoundingClientRect().x);

  const {
    isOpen: isOpenCreateLinkCard,
    onClose: onCloseCreateLinkCard,
    onOpen: onOpenCreateLinkCard,
  } = useDisclosure();

  const setOpenMenu = useCallback(
    (v: boolean) => {
      setTimeout(() => _setOpenMenu(v), 10);
    },
    [_setOpenMenu],
  );

  const activeLayout = useActiveLayout();

  const { direction } = useI18n();
  useEffect(() => {
    if (openMenu || openDialog) {
      return;
    }
    const x = view.dom.getBoundingClientRect().x;
    if (x !== offsetX) {
      setOffsetX(x);
    }
  }, [direction]);

  useEffect(() => {
    commands.setMenuOpened(openMenu);
  }, [openMenu]);

  const getCurrentNode = () => {
    const toolbars = document.getElementsByClassName('addBlocksToolbar');
    if (toolbars.length === 1) {
      const toolbar = toolbars.item(0);
      if (
        toolbar instanceof HTMLDivElement &&
        toolbar.hasAttribute('data-pos')
      ) {
        const pos = Number.parseInt(toolbar.getAttribute('data-pos')!);
        if (pos >= 0) {
          const nodeDom = view.nodeDOM(pos);
          try {
            if (isElementDomNode(nodeDom)) {
              let node = view.state.doc
                .resolve(view.posAtDOM(nodeDom, 0))
                .node();

              return { node, pos };
            }
          } catch (e) {
            return undefined;
          }
        }
      }
    }
  };

  const newPos = (pos: number) => {
    const node = view.nodeDOM(pos);

    if (!node || !isElementDomNode(node)) {
      return false;
    }

    const boundingRect = node.getBoundingClientRect();
    const topPos = boundingRect.top - 2;

    const toolbars = document.getElementsByClassName('addBlocksToolbar');
    if (toolbars.length === 1) {
      const toolbar = toolbars.item(0);
      if (toolbar instanceof HTMLDivElement) {
        if (toolbar.style.top !== `${topPos}px`) {
          toolbar.style.left = `${
            view.dom.getBoundingClientRect().x - (24 + 24)
          }px`;
          toolbar.style.top = `${topPos}px`;
          toolbar.style.opacity = '1';
          toolbar.setAttribute('data-pos', '' + pos);
        }
      }
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        zIndex: 99,
        opacity: 0,
        height: 24,
        width: 24,
      }}
      className="addBlocksToolbar"
    >
      <Menu>
        {({ isOpen }) => {
          setOpenMenu(isOpen);
          return (
            <>
              <MenuButton
                onClick={(e) => {
                  const currentNode = getCurrentNode();
                  const emptyParagraph =
                    currentNode?.node.type.name === 'paragraph' &&
                    currentNode.node.textContent === '';
                  // If we are not on an empty paragraph, first move to the next line
                  // The opened menu will just move along, this works fine
                  if (currentNode && !emptyParagraph) {
                    commands.handleNewItemButton(
                      currentNode.pos,
                      currentNode.node.nodeSize,
                    );

                    newPos(
                      currentNode.pos +
                        currentNode.node.nodeSize +
                        (currentNode.node.type.name === 'listItem' ? 1 : 0),
                    );
                  }
                }}
                as={IconButton}
                aria-label={t`Add editor item`}
                icon={<FontAwesomeIcon icon={solid('plus-large')} />}
                color={mode('gray.600', 'gray.400')}
                variant="ghost"
                id="editorMenuButton"
                size="xs"
              />
              <MenuList
                shadow="lg"
                py="4"
                color={mode('gray.600', 'gray.200')}
                px="3"
                marginLeft="44px"
                overflowY="scroll"
                maxH={300}
              >
                <MenuItem
                  icon={<TextIcon />}
                  onClick={() => {
                    setTimeout(() => {
                      const currentNode = getCurrentNode();
                      if (currentNode) {
                        if (
                          currentNode.node.type.name !== 'paragraph' ||
                          currentNode.node.textContent
                        ) {
                          chain.insertHtml('<p></p>', {
                            selection:
                              currentNode.pos + currentNode.node.nodeSize - 1,
                          });
                        }
                        chain.focus(
                          currentNode.pos + currentNode.node.nodeSize - 1,
                        );
                        chain.run();
                      }
                    }, 100);
                  }}
                >
                  {t`Paragraph`}
                </MenuItem>
                <MenuItem
                  icon={<H1Icon />}
                  onClick={() => {
                    setTimeout(() => {
                      const currentNode = getCurrentNode();
                      if (currentNode) {
                        chain.focus().run();
                        chain.toggleHeading({ level: 1 });
                        chain.focus(
                          currentNode.pos + currentNode.node.nodeSize - 1,
                        );
                        chain.run();
                      }
                    }, 100);
                  }}
                >
                  {t`Heading 1`}
                </MenuItem>
                <MenuItem
                  icon={<H2Icon />}
                  onClick={() => {
                    setTimeout(() => {
                      const currentNode = getCurrentNode();
                      if (currentNode) {
                        chain.focus().run();
                        chain.toggleHeading({ level: 2 });
                        chain.focus(
                          currentNode.pos + currentNode.node.nodeSize - 1,
                        );
                        chain.run();
                      }
                    }, 100);
                  }}
                >
                  {t`Heading 2`}
                </MenuItem>
                <MenuItem
                  icon={<H3Icon />}
                  onClick={() => {
                    setTimeout(() => {
                      const currentNode = getCurrentNode();
                      if (currentNode) {
                        chain.focus().run();
                        chain.toggleHeading({ level: 3 });
                        chain.focus(
                          currentNode.pos + currentNode.node.nodeSize - 1,
                        );
                        chain.run();
                      }
                    }, 100);
                  }}
                >
                  {t`Heading 3`}
                </MenuItem>
                <MenuItem
                  icon={<ListUlIcon />}
                  onClick={() => {
                    setTimeout(() => {
                      const currentNode = getCurrentNode();
                      if (currentNode) {
                        chain.focus().run();
                        chain.toggleBulletList();
                        chain.focus(
                          currentNode.pos + currentNode.node.nodeSize - 1,
                        );
                        chain.run();
                      }
                    }, 100);
                  }}
                >
                  {t`Bullet List`}
                </MenuItem>
                <MenuItem
                  icon={<ListOlIcon />}
                  onClick={() => {
                    setTimeout(() => {
                      const currentNode = getCurrentNode();
                      if (currentNode) {
                        chain.focus().run();
                        chain.toggleOrderedList();
                        chain.focus(
                          currentNode.pos + currentNode.node.nodeSize - 1,
                        );
                        chain.run();
                      }
                    }, 100);
                  }}
                >
                  {t`Ordered List`}
                </MenuItem>
                <MenuItem
                  icon={<HorizontalLineIcon />}
                  onClick={() => {
                    setTimeout(() => {
                      const currentNode = getCurrentNode();
                      if (currentNode) {
                        chain.focus().run();
                        chain.insertHorizontalRule();
                        chain.focus(
                          currentNode.pos + currentNode.node.nodeSize - 1,
                        );
                        chain.run();
                      }
                    }, 100);
                  }}
                >
                  {t`Line`}
                </MenuItem>
                <MenuItem
                  icon={<CompassIcon />}
                  onClick={() => {
                    setTimeout(() => {
                      onOpenCreateLinkCard();
                    }, 100);
                  }}
                >
                  {t`Create Link Card`}
                </MenuItem>
                {pageId && (
                  <>
                    <MenuItem
                      icon={<TodoIcon />}
                      onClick={() => {
                        setTimeout(() => {
                          const currentNode = getCurrentNode();
                          if (currentNode) {
                            chain.focus().run();
                            chain.toggleTodo({
                              selection: currentNode.pos,
                              pageId,
                            });
                            chain.focus(currentNode.pos + 1);
                            chain.run();
                          }
                        }, 100);
                      }}
                    >
                      {t`Add Todo`}
                    </MenuItem>
                    <AddBlocksToolbarAux
                      pageId={pageId}
                      currentNode={getCurrentNode()}
                      setOpenDialog={setOpenDialog}
                    />
                  </>
                )}
              </MenuList>
            </>
          );
        }}
      </Menu>
      <CreateLinkCardModal
        isOpen={isOpenCreateLinkCard}
        onClose={onCloseCreateLinkCard}
        setLinkCard={(url) => {
          const currentNode = getCurrentNode();
          if (currentNode) {
            chain.insertLinkCard({
              selection: currentNode.pos,
              url,
            });
            chain.focus(currentNode.pos + currentNode.node.nodeSize - 1);
            chain.run();
          }
        }}
      />
    </div>
  );
}

function AddBlocksToolbarAux({
  pageId,
  currentNode,
  setOpenDialog,
}: {
  pageId: string;
  currentNode?: NodeWithPosition;
  setOpenDialog: (status: boolean) => void;
}) {
  if (!currentNode) return null;

  const getCurrentNodePosition = () => {
    return currentNode.pos + currentNode.node.nodeSize - 1;
  };

  return (
    <>
      <AddImageBlock
        pageId={pageId}
        pos={getCurrentNodePosition()}
        setOpenDialog={setOpenDialog}
      />
      <AddFileBlock
        pageId={pageId}
        pos={getCurrentNodePosition()}
        setOpenDialog={setOpenDialog}
      />
    </>
  );
}
